import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { message } from "antd";
import { ArrowLeft, Monitor, Smartphone, Activity, Zap, Layout, Search, Shield, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiClient } from "@/services/apiClient";
import { useTenantStore } from "@/stores/useTenantStore";

// ============================================================================
// Types
// ============================================================================

interface PSIMetrics {
	performance_score: number | null;
	fcp: number | null;
	lcp: number | null;
	cls: number | null;
	tbt: number | null;
	speed_index: number | null;
	tti: number | null;
}

interface PSIResult {
	id?: string;
	url: string;
	strategy: string;
	metrics: PSIMetrics;
	category_scores: Record<string, number>;
	analyzed_at: string;
}

// ============================================================================
// Thresholds (Google's official CWV thresholds)
// ============================================================================

type Rating = "good" | "needs-improvement" | "poor";

function rateMetric(
	value: number | null,
	goodThreshold: number,
	poorThreshold: number,
): Rating {
	if (value === null) return "poor";
	if (value <= goodThreshold) return "good";
	if (value <= poorThreshold) return "needs-improvement";
	return "poor";
}

function ratePerformance(score: number | null): Rating {
	if (score === null) return "poor";
	if (score >= 90) return "good";
	if (score >= 50) return "needs-improvement";
	return "poor";
}

const ratingColors: Record<Rating, string> = {
	good: "#22c55e",
	"needs-improvement": "#f59e0b",
	poor: "#ef4444",
};

const ratingBgColors: Record<Rating, string> = {
	good: "bg-green-50 dark:bg-green-950/40",
	"needs-improvement": "bg-amber-50 dark:bg-amber-950/40",
	poor: "bg-red-50 dark:bg-red-950/40",
};

const ratingBorderColors: Record<Rating, string> = {
	good: "border-green-200 dark:border-green-800",
	"needs-improvement": "border-amber-200 dark:border-amber-800",
	poor: "border-red-200 dark:border-red-800",
};

const ratingTextColors: Record<Rating, string> = {
	good: "text-green-600 dark:text-green-400",
	"needs-improvement": "text-amber-600 dark:text-amber-400",
	poor: "text-red-600 dark:text-red-400",
};

const ratingLabels: Record<Rating, string> = {
	good: "Good",
	"needs-improvement": "Needs Improvement",
	poor: "Poor",
};

// ============================================================================
// Animated Gauge Circle Component
// ============================================================================

function AnimatedGaugeCircle({
	score,
	size = 200,
	label,
	isAnimating,
}: {
	score: number | null;
	size?: number;
	label: string;
	isAnimating: boolean;
}) {
	const [animatedScore, setAnimatedScore] = useState(0);
	const targetScore = score ?? 0;
	const rating = ratePerformance(score);
	const color = ratingColors[rating];

	useEffect(() => {
		if (!isAnimating) {
			// Avoid synchronous setState in effect body by using requestAnimationFrame
			const rafId = requestAnimationFrame(() => {
				setAnimatedScore(targetScore);
			});
			return () => cancelAnimationFrame(rafId);
		}

		const duration = 1200;
		const startTime = performance.now();
		const startValue = 0;

		const animate = (currentTime: number) => {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const easeOutQuart = 1 - Math.pow(1 - progress, 4);
			const currentScore = Math.round(startValue + (targetScore - startValue) * easeOutQuart);
			setAnimatedScore(currentScore);

			if (progress < 1) {
				requestAnimationFrame(animate);
			}
		};

		const rafId = requestAnimationFrame(animate);
		return () => cancelAnimationFrame(rafId);
	}, [targetScore, isAnimating]);

	const radius = (size - 16) / 2;
	const circumference = 2 * Math.PI * radius;
	const progress = (animatedScore / 100) * circumference;
	const offset = circumference - progress;

	return (
		<div className="flex flex-col items-center">
			<div className="relative" style={{ width: size, height: size }}>
				<svg width={size} height={size} className="transform -rotate-90">
					<circle
						cx={size / 2}
						cy={size / 2}
						r={radius}
						fill="none"
						stroke="#f3f4f6"
						strokeWidth="12"
					/>
					<circle
						cx={size / 2}
						cy={size / 2}
						r={radius}
						fill="none"
						stroke={color}
						strokeWidth="12"
						strokeLinecap="round"
						strokeDasharray={circumference}
						strokeDashoffset={offset}
						className="transition-all duration-300"
					/>
				</svg>
				<div className="absolute inset-0 flex flex-col items-center justify-center">
					<span className="text-5xl font-bold" style={{ color }}>
						{score === null ? "--" : animatedScore}
					</span>
				</div>
			</div>
			<span className="text-sm font-medium text-muted-foreground mt-3">{label}</span>
		</div>
	);
}

// ============================================================================
// Core Web Vitals Card Component
// ============================================================================

interface CWVCardProps {
	label: string;
	value: number | null;
	unit: string;
	description: string;
	goodThreshold: number;
	poorThreshold: number;
	icon: React.ReactNode;
}

function CWVCard({
	label,
	value,
	unit,
	description,
	goodThreshold,
	poorThreshold,
	icon,
}: CWVCardProps) {
	const rating = rateMetric(value, goodThreshold, poorThreshold);
	const color = ratingColors[rating];
	const bgClass = ratingBgColors[rating];
	const borderClass = ratingBorderColors[rating];
	const textClass = ratingTextColors[rating];

	const formatValue = (v: number | null) => {
		if (v === null) return "--";
		if (unit === "s") return (v / 1000).toFixed(1);
		if (unit === "ms") return Math.round(v).toString();
		return v.toFixed(3);
	};

	const formatUnit = unit === "s" ? "s" : unit === "ms" ? "ms" : "";

	return (
		<div className={`rounded-xl border ${borderClass} ${bgClass} p-5 shadow-sm hover:shadow-md transition-shadow`}>
			<div className="flex items-start justify-between mb-3">
				<div className={`p-2 rounded-lg ${bgClass} ${textClass}`}>
					{icon}
				</div>
				<span
					className="text-xs font-semibold px-2.5 py-1 rounded-full"
					style={{ backgroundColor: color, color: "white" }}
				>
					{ratingLabels[rating]}
				</span>
			</div>
			<div className="mb-2">
				<span className="text-3xl font-bold" style={{ color }}>
					{formatValue(value)}
				</span>
				{formatUnit && <span className="text-sm text-muted-foreground ml-1">{formatUnit}</span>}
			</div>
			<h4 className="text-sm font-semibold text-foreground mb-1">{label}</h4>
			<p className="text-xs text-muted-foreground">{description}</p>
		</div>
	);
}

// ============================================================================
// Additional Metric Card Component
// ============================================================================

function MetricCard({
	label,
	value,
	unit,
	goodThreshold,
	poorThreshold,
}: {
	label: string;
	value: number | null;
	unit: string;
	goodThreshold: number;
	poorThreshold: number;
}) {
	const rating = rateMetric(value, goodThreshold, poorThreshold);
	const color = ratingColors[rating];

	const formatValue = (v: number | null) => {
		if (v === null) return "--";
		if (unit === "s") return (v / 1000).toFixed(1);
		if (unit === "ms") return Math.round(v).toString();
		return v.toFixed(3);
	};

	const formatUnit = unit === "s" ? "s" : unit === "ms" ? "ms" : "";

	return (
		<div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border shadow-sm">
			<span className="text-sm font-medium text-foreground">{label}</span>
			<div className="flex items-baseline gap-1">
				<span className="text-lg font-bold" style={{ color }}>
					{formatValue(value)}
				</span>
				{formatUnit && <span className="text-xs text-muted-foreground">{formatUnit}</span>}
			</div>
		</div>
	);
}

// ============================================================================
// Category Score Bar Component
// ============================================================================

function CategoryScoreBar({
	label,
	score,
	icon,
}: {
	label: string;
	score: number | undefined;
	icon: React.ReactNode;
}) {
	const rating = ratePerformance(score ?? null);
	const color = ratingColors[rating];
	const percentage = score ?? 0;

	return (
		<div className="bg-card rounded-xl border border-border p-4 shadow-sm">
			<div className="flex items-center gap-3 mb-3">
				<div className="p-2 bg-muted rounded-lg text-muted-foreground">
					{icon}
				</div>
				<span className="text-sm font-semibold text-foreground">{label}</span>
			</div>
			<div className="flex items-center gap-3">
				<div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
					<div
						className="h-full rounded-full transition-all duration-700 ease-out"
						style={{ width: `${percentage}%`, backgroundColor: color }}
					/>
				</div>
				<span
					className="text-sm font-bold min-w-[2rem] text-right"
					style={{ color }}
				>
					{score ?? "--"}
				</span>
			</div>
		</div>
	);
}

// ============================================================================
// Main Page Component
// ============================================================================

export default function CoreWebVitalsPage() {
	const navigate = useNavigate();
	const { currentTenant } = useTenantStore();
	const [url, setUrl] = useState("");
	const [strategy, setStrategy] = useState<"mobile" | "desktop">("mobile");
	const [result, setResult] = useState<PSIResult | null>(null);
	const [isAnimating, setIsAnimating] = useState(false);

	const analyzeMutation = useMutation({
		mutationFn: async ({
			url,
			strategy,
		}: {
			url: string;
			strategy: string;
		}) => {
			const resp = await apiClient.post<PSIResult>("/pagespeed/analyze", {
				url,
				strategy,
				tenant_id: currentTenant || undefined,
			});
			return resp.data;
		},
		onSuccess: (data) => {
			setResult(data);
			setIsAnimating(true);
			setTimeout(() => setIsAnimating(false), 1500);
		},
		onError: (err: unknown) => {
			const errorMessage = err && typeof err === "object" && "response" in err
				? (err.response as { data?: { detail?: string } })?.data?.detail
				: "Analysis failed. Please try again.";
			message.error(errorMessage);
		},
	});

	const handleAnalyze = () => {
		let normalizedUrl = url.trim();
		if (!normalizedUrl) {
			message.warning("Please enter a URL");
			return;
		}
		if (
			!normalizedUrl.startsWith("http://") &&
			!normalizedUrl.startsWith("https://")
		) {
			normalizedUrl = "https://" + normalizedUrl;
		}
		analyzeMutation.mutate({ url: normalizedUrl, strategy });
	};

	const metrics = result?.metrics;

	return (
		<div className="min-h-screen bg-muted">
			{/* Header */}
			<div className="bg-card border-b px-6 py-4">
				<div className="flex items-center gap-4 max-w-6xl mx-auto">
					<button
						onClick={() => navigate(-1)}
						className="p-2 hover:bg-muted rounded-lg transition-colors"
					>
						<ArrowLeft className="w-5 h-5 text-muted-foreground" />
					</button>
					<div className="flex items-center gap-3">
						<div className="p-2 bg-orange-100 dark:bg-orange-900/40 rounded-lg">
							<Activity className="w-5 h-5 text-orange-500 dark:text-orange-400" />
						</div>
						<div>
							<h1 className="text-xl font-bold text-foreground">Core Web Vitals</h1>
							<p className="text-sm text-muted-foreground">
								Analyze page performance metrics that affect your Google rankings
							</p>
						</div>
					</div>
				</div>
			</div>

			<div className="max-w-6xl mx-auto p-6">
				{/* URL Input Card */}
				<div className="bg-card rounded-2xl shadow-sm border border-border p-6 mb-6">
					<div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
						<div className="flex-1 relative">
							<div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
								<Search className="w-5 h-5" />
							</div>
							<Input
								placeholder="Enter website URL (e.g., example.com)"
								value={url}
								onChange={(e) => setUrl(e.target.value)}
								onKeyDown={(e) => e.key === "Enter" && handleAnalyze()}
								className="pl-12 py-6 text-base border-border focus:border-orange-500 focus:ring-orange-500 rounded-xl"
							/>
						</div>

						{/* Device Toggle Tabs */}
						<div className="flex items-center bg-muted rounded-xl p-1">
							<button
								className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
									strategy === "mobile"
										? "bg-card text-foreground shadow-sm"
										: "text-muted-foreground hover:text-foreground"
								}`}
								onClick={() => setStrategy("mobile")}
							>
								<Smartphone className="w-4 h-4" />
								Mobile
							</button>
							<button
								className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
									strategy === "desktop"
										? "bg-card text-foreground shadow-sm"
										: "text-muted-foreground hover:text-foreground"
								}`}
								onClick={() => setStrategy("desktop")}
							>
								<Monitor className="w-4 h-4" />
								Desktop
							</button>
						</div>

						<Button
							onClick={handleAnalyze}
							disabled={analyzeMutation.isPending}
							className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all"
						>
							{analyzeMutation.isPending ? (
								<span className="flex items-center gap-2">
									<span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
									Analyzing...
								</span>
							) : (
								"Analyze"
							)}
						</Button>
					</div>
				</div>

				{/* Loading State */}
				{analyzeMutation.isPending && (
					<div className="bg-card rounded-2xl shadow-sm border border-border p-16">
						<div className="flex flex-col items-center justify-center">
							<div className="relative w-20 h-20 mb-6">
								<div className="absolute inset-0 border-4 border-border rounded-full" />
								<div className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
							</div>
							<h3 className="text-lg font-semibold text-foreground mb-2">
								Analyzing {strategy === "mobile" ? "Mobile" : "Desktop"} Performance
							</h3>
							<p className="text-sm text-muted-foreground">
								This may take 15-30 seconds
							</p>
						</div>
					</div>
				)}

				{/* Empty State */}
				{!result && !analyzeMutation.isPending && (
					<div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
						<div className="p-12 text-center">
							<div className="w-20 h-20 mx-auto mb-6 bg-muted rounded-full flex items-center justify-center">
								<Activity className="w-10 h-10 text-gray-300" />
							</div>
							<h2 className="text-xl font-bold text-foreground mb-2">
								Check Your Page Performance
							</h2>
							<p className="text-sm text-muted-foreground max-w-md mx-auto mb-10">
								Enter a URL above to analyze Core Web Vitals - the metrics Google
								uses to measure user experience and ranking.
							</p>

							<div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
								<div className="p-5 rounded-xl bg-muted border border-border text-left">
									<div className="flex items-center gap-2 mb-2">
										<div className="w-2 h-2 rounded-full bg-green-500" />
										<h3 className="font-semibold text-foreground">LCP</h3>
									</div>
									<p className="text-xs text-muted-foreground leading-relaxed">
										Largest Contentful Paint. Measures loading performance. Aim for &le;2.5 seconds.
									</p>
								</div>
								<div className="p-5 rounded-xl bg-muted border border-border text-left">
									<div className="flex items-center gap-2 mb-2">
										<div className="w-2 h-2 rounded-full bg-amber-500" />
										<h3 className="font-semibold text-foreground">TBT</h3>
									</div>
									<p className="text-xs text-muted-foreground leading-relaxed">
										Total Blocking Time. Measures interactivity. Aim for &le;200 milliseconds.
									</p>
								</div>
								<div className="p-5 rounded-xl bg-muted border border-border text-left">
									<div className="flex items-center gap-2 mb-2">
										<div className="w-2 h-2 rounded-full bg-red-500" />
										<h3 className="font-semibold text-foreground">CLS</h3>
									</div>
									<p className="text-xs text-muted-foreground leading-relaxed">
										Cumulative Layout Shift. Measures visual stability. Aim for &le;0.1 score.
									</p>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Results */}
				{result && !analyzeMutation.isPending && metrics && (
					<div className="space-y-6">
						{/* Results Header */}
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-muted-foreground">
							<div className="flex items-center gap-2">
								<span className="font-medium text-foreground">{result.url}</span>
							</div>
							<div className="flex items-center gap-2">
								<span className="flex items-center gap-1">
									{result.strategy === "mobile" ? (
										<Smartphone className="w-3.5 h-3.5" />
									) : (
										<Monitor className="w-3.5 h-3.5" />
									)}
									{result.strategy === "mobile" ? "Mobile" : "Desktop"}
								</span>
								<span>&middot;</span>
								<span>{new Date(result.analyzed_at).toLocaleString()}</span>
							</div>
						</div>

						{/* Main Score and Category Scores */}
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
							{/* Performance Score Gauge */}
							<div className="bg-card rounded-2xl shadow-sm border border-border p-8 flex flex-col items-center justify-center">
								<AnimatedGaugeCircle
									score={metrics.performance_score}
									size={180}
									label="Performance Score"
									isAnimating={isAnimating}
								/>
							</div>

							{/* Category Scores */}
							<div className="lg:col-span-2 bg-card rounded-2xl shadow-sm border border-border p-6">
								<h3 className="text-sm font-semibold text-foreground mb-4">Category Scores</h3>
								<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
									<CategoryScoreBar
										label="Accessibility"
										score={result.category_scores.accessibility}
										icon={<Shield className="w-4 h-4" />}
									/>
									<CategoryScoreBar
										label="Best Practices"
										score={result.category_scores["best-practices"]}
										icon={<Sparkles className="w-4 h-4" />}
									/>
									<CategoryScoreBar
										label="SEO"
										score={result.category_scores.seo}
										icon={<Search className="w-4 h-4" />}
									/>
								</div>
							</div>
						</div>

						{/* Core Web Vitals */}
						<div className="bg-card rounded-2xl shadow-sm border border-border p-6">
							<div className="flex items-center gap-2 mb-5">
								<div className="p-2 bg-orange-100 dark:bg-orange-900/40 rounded-lg">
									<Zap className="w-5 h-5 text-orange-500 dark:text-orange-400" />
								</div>
								<h3 className="text-lg font-bold text-foreground">Core Web Vitals</h3>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
								<CWVCard
									label="Largest Contentful Paint"
									value={metrics.lcp}
									unit="s"
									description="Measures loading performance. Good scores are &le;2.5s."
									goodThreshold={2500}
									poorThreshold={4000}
									icon={<Layout className="w-5 h-5" />}
								/>
								<CWVCard
									label="Total Blocking Time"
									value={metrics.tbt}
									unit="ms"
									description="Measures interactivity. Good scores are &le;200ms."
									goodThreshold={200}
									poorThreshold={600}
									icon={<Activity className="w-5 h-5" />}
								/>
								<CWVCard
									label="Cumulative Layout Shift"
									value={metrics.cls}
									unit=""
									description="Measures visual stability. Good scores are &le;0.1."
									goodThreshold={0.1}
									poorThreshold={0.25}
									icon={<Layout className="w-5 h-5" />}
								/>
							</div>
						</div>

						{/* Additional Metrics */}
						<div className="bg-card rounded-2xl shadow-sm border border-border p-6">
							<div className="flex items-center gap-2 mb-5">
								<div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
									<Activity className="w-5 h-5 text-blue-500 dark:text-blue-400" />
								</div>
								<h3 className="text-lg font-bold text-foreground">Additional Metrics</h3>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
								<MetricCard
									label="First Contentful Paint"
									value={metrics.fcp}
									unit="s"
									goodThreshold={1800}
									poorThreshold={3000}
								/>
								<MetricCard
									label="Speed Index"
									value={metrics.speed_index}
									unit="s"
									goodThreshold={3400}
									poorThreshold={5800}
								/>
								<MetricCard
									label="Time to Interactive"
									value={metrics.tti}
									unit="s"
									goodThreshold={3800}
									poorThreshold={7300}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
